/**
 * CompositorMasks.js
 * Wallet-grip mask loading, creation, and utility functions
 */

/**
 * Calculates the bounding box of opaque pixels in a canvas
 * Used to determine the grip area bounds from the inverted mask
 *
 * @param {HTMLCanvasElement} canvas - The canvas to analyze
 * @returns {Object|null} Bounding box {x, y, width, height} or null if no opaque pixels
 */
export function getOpaqueBounds(canvas) {
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let minX = canvas.width;
    let minY = canvas.height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
            const alpha = data[(y * canvas.width + x) * 4 + 3];
            if (alpha > 128) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }

    if (maxX < minX || maxY < minY) {
        return null;
    }

    return {
        x: minX,
        y: minY,
        width: maxX - minX + 1,
        height: maxY - minY + 1
    };
}

/**
 * Loads the wallet-grip mask image for surface-specific rendering
 * The mask has transparent pixels where the grip is and opaque pixels where the wallet is
 *
 * @returns {Promise<HTMLImageElement>} The loaded mask image
 */
export async function loadWalletGripMask() {
    const maskUrl = new URL("./assets/wallet-grip-mask.png", import.meta.url).href;
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = maskUrl;
    });
}

/**
 * Creates an inverted version of a mask image
 * Inverts transparent/opaque areas so grip becomes opaque and wallet becomes transparent
 *
 * @param {HTMLImageElement} maskImage - The original mask image
 * @param {number} targetWidth - The target width to scale the mask to
 * @param {number} targetHeight - The target height to scale the mask to
 * @returns {HTMLCanvasElement} A canvas with the inverted mask
 */
export function createInvertedMask(maskImage, targetWidth, targetHeight) {
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    ctx.globalCompositeOperation = "destination-out";

    // Scale mask proportionally to cover target (maintaining aspect ratio)
    // This ensures the mask aligns correctly with the product image
    const maskAspect = maskImage.width / maskImage.height;
    const targetAspect = targetWidth / targetHeight;
    let drawWidth, drawHeight, drawX, drawY;

    if (maskAspect > targetAspect) {
        // Mask is wider - fit to height, crop sides
        drawHeight = targetHeight;
        drawWidth = targetHeight * maskAspect;
        drawX = (targetWidth - drawWidth) / 2;
        drawY = 0;
    } else {
        // Mask is taller - fit to width, crop top/bottom
        drawWidth = targetWidth;
        drawHeight = targetWidth / maskAspect;
        drawX = 0;
        drawY = (targetHeight - drawHeight) / 2;
    }

    ctx.drawImage(maskImage, drawX, drawY, drawWidth, drawHeight);
    ctx.globalCompositeOperation = "source-over";
    return canvas;
}

/**
 * Creates a scaled version of a mask image as a canvas
 *
 * @param {HTMLImageElement} maskImage - The original mask image
 * @param {number} targetWidth - The target width to scale the mask to
 * @param {number} targetHeight - The target height to scale the mask to
 * @returns {HTMLCanvasElement} A canvas with the scaled mask
 */
export function createScaledMask(maskImage, targetWidth, targetHeight) {
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");

    // Scale mask proportionally to cover target (maintaining aspect ratio)
    // This ensures the mask aligns correctly with the product image
    const maskAspect = maskImage.width / maskImage.height;
    const targetAspect = targetWidth / targetHeight;
    let drawWidth, drawHeight, drawX, drawY;

    if (maskAspect > targetAspect) {
        // Mask is wider - fit to height, crop sides
        drawHeight = targetHeight;
        drawWidth = targetHeight * maskAspect;
        drawX = (targetWidth - drawWidth) / 2;
        drawY = 0;
    } else {
        // Mask is taller - fit to width, crop top/bottom
        drawWidth = targetWidth;
        drawHeight = targetWidth / maskAspect;
        drawX = 0;
        drawY = (targetHeight - drawHeight) / 2;
    }

    ctx.drawImage(maskImage, drawX, drawY, drawWidth, drawHeight);
    return canvas;
}
